import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Container, Flex, Heading, Section, Text } from '@radix-ui/themes';
import { selectDevice } from 'store/slices/common';
import { localList, sitemap } from 'app/constants';
import { formatYMD } from 'utils/date';

import Visual from 'components/common/visual';
import PageTitle from 'components/layout/pageTitle';
import SocialShare from 'components/common/socialShare';

import 'components/common/breadcrumbs/style.scss';
import 'components/board/boardDetail/style.scss';

// boardType 별 페이지 메타데이터 (Notice/Event/Education/Promotion Detail.jsx와 동일)
const BOARD_META = {
  notice: { pageName: '공지사항', icon: 'bubble' },
  event: { pageName: '행사정보', icon: 'event' },
  education: { pageName: '교육정보', icon: 'education' },
  promotion: { pageName: '홍보자료', icon: 'star' }
};

// 관리자에서 보낸 postMessage origin 화이트리스트 (개발용 기본값 + env)
const ALLOWED_ORIGINS = (process.env.REACT_APP_ADMIN_ORIGINS || 'http://localhost:3001,http://localhost:3000')
  .split(',')
  .map(s => s.trim())
  .filter(Boolean);

// 본문 HTML의 img alt 보강 (BoardDetail.parseContentWithAlt 와 동일한 규칙)
function parseContentWithAlt(htmlString, postTitle) {
  if (!htmlString || !htmlString.trim()) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  doc.querySelectorAll('img').forEach(img => {
    const currentAlt = img.getAttribute('alt')?.trim();
    const isFileName = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(currentAlt);
    if (!currentAlt || isFileName) {
      img.setAttribute('alt', postTitle || '');
    }
  });

  doc.querySelectorAll('a[target="_blank"]').forEach(link => {
    const title = link.getAttribute('title');
    if (!title || !title.trim()) {
      link.setAttribute('title', '새 창 열림');
    }
  });

  return doc.body.innerHTML;
}

// 게시글 미리보기 페이지 - 관리자에서 iframe + postMessage 로 호출됨
export default function Preview() {
  const deviceType = useSelector(selectDevice);

  const [draft, setDraft] = useState(null);
  // 관리자에서 선택한 viewport('pc' | 'mobile'). 미리보기는 UA 가 아니라 이 값으로 모바일 여부를 판단한다.
  const [viewport, setViewport] = useState(null);

  // viewport 미지정 시에는 기존 동작(redux device)으로 폴백.
  const isMobile = viewport ? viewport === 'mobile' : deviceType === 'mobile';

  // postMessage 수신 + 준비 완료 신호 송신
  useEffect(() => {
    const handleMessage = event => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) return;
      const data = event.data;
      if (!data) return;
      // 화면 크기(PC/모바일) 토글 - 데이터 재전송 없이 viewport 만 갱신
      if (data.type === 'PREVIEW_VIEWPORT') {
        if (data.viewport) setViewport(data.viewport);
        return;
      }
      if (data.type !== 'PREVIEW_DATA') return;
      setDraft(data.payload || null);
      if (data.payload?.viewport) setViewport(data.payload.viewport);
    };

    window.addEventListener('message', handleMessage);

    // 관리자 쪽 onLoad 보다 먼저 마운트가 끝나도 안전하게 받을 수 있도록 ready 신호 송신
    try {
      window.parent?.postMessage({ type: 'PREVIEW_READY' }, '*');
    } catch (_) {
      // noop
    }

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // 미리보기는 "정지된 스냅샷"이어야 한다. iframe 안에서 헤더 메뉴/브레드크럼/로고/푸터 등 내부 이동 링크를
  // 누르면 iframe 이 실제 페이지로 이동하면서 미리보기(메모리에만 있는 draft)가 사라지므로, 같은 프레임을
  // 이동시키는 링크 클릭만 차단한다.
  //  - <a> 만 대상 (버튼: 목록/공유 등은 그대로 동작)
  //  - target="_blank"(새 탭으로 열리는 관련링크/공유 링크)는 미리보기를 깨지 않으므로 허용
  //  - 캡처 단계에서 preventDefault → 네이티브 이동과 react-router <Link>(defaultPrevented 시 이동 안 함) 모두 차단
  useEffect(() => {
    const blockNavigation = event => {
      const anchor = event.target.closest?.('a');
      if (!anchor) return;
      if (anchor.getAttribute('target') === '_blank') return;
      event.preventDefault();
    };
    document.addEventListener('click', blockNavigation, true);
    return () => document.removeEventListener('click', blockNavigation, true);
  }, []);

  // 선택한 viewport 를 html[data-device] 에 반영해 모바일 전용 CSS(html[data-device='mobile'])가 적용되도록 한다.
  // (/preview 에선 useDevice 의 UA 자동판별이 꺼져 있어 이 값이 덮어써지지 않는다)
  // 참고: 공유 레이아웃(Header/Footer/Visual/PageTitle)은 redux device 로 마크업을 분기하지만, 미리보기는
  // 시각 확인용이므로 CSS(data-device) 수준 전환으로 충분하다. redux device 까지 강제 전환하면 Lottie 등
  // 무거운 모바일 렌더가 동반돼 미리보기가 불안정해질 수 있어 의도적으로 하지 않는다.
  useEffect(() => {
    if (!viewport) return;
    document.documentElement.setAttribute('data-device', viewport);
  }, [viewport]);

  const boardType = draft?.boardType || 'notice';
  const meta = BOARD_META[boardType] || BOARD_META.notice;

  const post = draft?.post || {};
  const files = draft?.files || [];
  const thumbnails = draft?.thumbnails || [];

  const parsedContent = useMemo(() => parseContentWithAlt(post.content || '', post.title || ''), [post.content, post.title]);

  const createPostDate = post.createDate ? formatYMD(post.createDate) : formatYMD(new Date().toISOString());
  const regionLabel = post.region || '전국';
  const regionEn = localList.find(local => local.name === regionLabel)?.en || 'all';

  // boardType 에 해당하는 사이트맵 항목으로 breadcrumb 구성 (실제 페이지의 Breadcrumbs 와 동일한 마크업).
  const breadcrumb = useMemo(() => {
    const basePath = `/${boardType}`;
    const data = sitemap.find(item => item.depth1.url === basePath || item.depth2.some(sub => sub.url === basePath));
    const depth2Match = data?.depth2?.find(sub => sub.url === basePath) || null;
    return { depth1Name: data?.depth1?.name || '', depth2Name: depth2Match?.name || '' };
  }, [boardType]);

  return (
    <>
      <Visual category={boardType} title={meta.pageName} />
      {!isMobile && (
        <section>
          <Container size="4">
            <Flex className="breadcrumbs">
              <Link to="/" title="메인으로 이동" className="home">
                메인
              </Link>
              {breadcrumb.depth1Name && (
                <div className="button-wrap">
                  <span className="body2">{breadcrumb.depth1Name}</span>
                  {breadcrumb.depth2Name && <span className="body2">{breadcrumb.depth2Name}</span>}
                </div>
              )}
            </Flex>
          </Container>
        </section>
      )}
      <Section p="0" pb={isMobile ? 'var(--space-18)' : 'var(--space-35)'} className={boardType}>
        <Container size={isMobile ? '1' : '4'}>
          <PageTitle icon={meta.icon} title={meta.pageName} />

          {/* 미리보기 안내 배너 */}
          <Box
            mb="4"
            p="2"
            style={{
              background: 'var(--accent-3)',
              border: '1px dashed var(--accent-8)',
              borderRadius: 'var(--radius-3)',
              textAlign: 'center'
            }}
          >
            <Text as="p" size="2" color="indigo">
              미리보기 화면입니다. 실제 등록된 게시글이 아닙니다.
            </Text>
          </Box>

          {/* 아래는 BoardDetail 의 렌더링 부분과 동일한 마크업 */}
          <Box className="post-title">
            <Flex direction={isMobile && 'column'} justify="center" align="center" gap={isMobile ? '2' : '6'}>
              <Box className={`chips local ${regionEn}`}>
                <Text>{regionLabel}</Text>
              </Box>
              <Heading as="h3" className={isMobile ? 'sub-title' : 'title3'}>
                {post.title || '(제목 없음)'}
              </Heading>
            </Flex>
          </Box>

          <Box className="post-info">
            <Text as="p">등록일 : {createPostDate}</Text>
            <Text as="p">조회수 : 0</Text>
          </Box>

          <Box className="post-box">
            <Box className="post-content">
              <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
            </Box>
          </Box>

          <Box className="post-attachment">
            {files.length > 0 && (
              <Flex direction={isMobile && 'column'}>
                <Box>
                  <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-clip`}>
                    첨부파일
                  </Text>
                </Box>
                <ul className="attachment-list">
                  {files.map((file, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        className="file-download-btn"
                        aria-label={`${file.originalName} 다운로드 (미리보기에서는 비활성)`}
                        disabled
                        onClick={e => e.preventDefault()}
                        style={{ opacity: 0.85, cursor: 'not-allowed' }}
                        title="미리보기에서는 다운로드할 수 없습니다."
                      >
                        <span className={`icon icon-${file.extension || ''}`} aria-hidden="true" />
                        <Text as="p" className="file">
                          {file.originalName}
                        </Text>
                        <span className="download" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </Flex>
            )}
            {post.link && (
              <Flex direction={isMobile && 'column'}>
                <Box>
                  <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-link`}>
                    관련링크
                  </Text>
                </Box>
                <ul className="attachment-list">
                  <li>
                    <a
                      href={post.link.startsWith('http') ? post.link : `https://${post.link}`}
                      target="_blank"
                      color="gray"
                      rel="noreferrer"
                      title="새 창 열림"
                    >
                      <Text as="p" className="link">
                        {post.link.startsWith('http') ? post.link : `https://${post.link}`}
                      </Text>
                    </a>
                  </li>
                </ul>
              </Flex>
            )}
          </Box>

          <Flex direction={isMobile && 'column'} justify="end" align="center" className="sns-list">
            <SocialShare
              title={post.title || '게시글'}
              url={window.location.href}
              description={(post.content || '').replace(/<[^>]*>/g, '').substring(0, 100) + '...'}
              imageUrl={thumbnails[0]?.fileUrl || ''}
            />
            {/* 미리보기에서는 실제와 동일하게 보이되 동작은 하지 않는다(목록 이동 X). disabled 로 흐리게 두면
                글자가 묻혀 안 보이므로, 실제 버튼과 같은 모양으로 두고 onClick 만 두지 않는다. */}
            <Button className="register" type="button" aria-disabled="true" style={{ cursor: 'default' }}>
              목록
            </Button>
          </Flex>
        </Container>
      </Section>
    </>
  );
}
